import { computed, type MaybeRefOrGetter, toValue } from 'vue'
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/vue-query'
import { http, isApiError, unwrap } from './client'
import type { ApiEnvelope } from '@/types/api'
import type { InstrumentationGap, MetricReadiness } from '@/types/intelligence'
import type { MetricChange, MetricNote, PeriodKind, PeriodSelection } from './snapshot'

/** P3 §2.1's filters. */
export type StatusFilter = 'ALL' | 'ACTIVE' | 'RESOLVED' | 'DISMISSED'
/** The Overview's perspectives a rule finds opportunities in; the API lists them (Care quality since DECISIONS.md #45). */
export type Perspective = 'OPERATIONS' | 'CARE_QUALITY'
export type DomainFilter = 'ALL' | Perspective
export type PriorityFilter = 'ALL' | Priority

export interface OpportunityFilters extends PeriodSelection {
  status: StatusFilter
  domain: DomainFilter
  priority: PriorityFilter
}

/** S0 §10.2: a governed state with a reason, never a number. */
export type Priority = 'HIGH' | 'MEDIUM' | 'LOW'
export type OpportunityStatus = 'NEW' | 'REOPENED' | 'RESOLVED' | 'DISMISSED'
export type IntelligenceDomain = 'PATIENT_FLOW' | 'CARE_CONTINUITY'
export type DismissalReason =
  | 'EXPECTED_BEHAVIOUR'
  | 'KNOWN_OPERATIONAL_CONTEXT'
  | 'INSUFFICIENT_SIGNIFICANCE'
  | 'INCORRECT_INTERPRETATION'
  | 'INSUFFICIENT_DATA'
  | 'DUPLICATE'
  | 'NO_ACTION_REQUIRED'
  | 'OTHER'
export const DISMISSAL_REASONS: DismissalReason[] = [
  'EXPECTED_BEHAVIOUR',
  'KNOWN_OPERATIONAL_CONTEXT',
  'INSUFFICIENT_SIGNIFICANCE',
  'INCORRECT_INTERPRETATION',
  'INSUFFICIENT_DATA',
  'DUPLICATE',
  'NO_ACTION_REQUIRED',
  'OTHER',
]

/** "Why surfaced": the priority rule's line and its figures. `week` is the worse week it refers to, by its Monday. */
export type PriorityRationale =
  | { code: 'WORSE_WEEKS_IN_A_ROW'; weeks: number; week: string }
  | { code: 'ABOVE_USUAL'; percent: number; week: string }
  | { code: 'WORSE_LAST_WEEK'; week: string }

/** "Why surfaced" for the next-step rule (DECISIONS.md #45): `week` is the lower week it refers to. */
export type ShareRationale =
  | { code: 'FEWER_WEEKS_IN_A_ROW'; weeks: number; week: string }
  | { code: 'BELOW_USUAL'; points: number; week: string }
  | { code: 'FEWER_LAST_WEEK'; week: string }

export type BreakdownAxis = 'DAY' | 'HOUR' | 'ROLE' | 'VISIT_TYPE'

export interface Concentration {
  axis: BreakdownAxis
  /** One group, or for hours a run of next-door hours. */
  groups: string[]
  top: string
  riseMinutes: number
  overallRiseMinutes: number
}

interface OpportunityCommon {
  id: string
  domain: IntelligenceDomain
  perspective: Perspective
  metricId: string
  status: OpportunityStatus
  priority: Priority
  prioritizationRuleVersion: string
  readiness: MetricReadiness
  firstWeek: string
  lastWeek: string
  resolvedAt: string | null
  dismissal: { reason: DismissalReason; note: string | null; at: string } | null
  recurrenceOfId: string | null
  /** Follow-ups assigned from it, dismissed ones apart: "Assigned" with one or more (Dao, 1 Oct 2026). */
  followUps: number
}

/** Waiting time rose (SIGNAL-OP-WAITING-TIME-WORSE). */
export interface WaitingTimeSummary extends OpportunityCommon {
  kind: 'WAITING_TIME'
  rationale: PriorityRationale
  latestMinutes: number
  baselineMinutes: number
  percentAbove: number | null
  concentration: Concentration | null
  affectedVisits: number
  notes: MetricNote[]
}

/** Fewer chronic patients have a next step (SIGNAL-CONT-NEXT-STEP-LOWER, DECISIONS.md #45). */
export interface NextStepSummary extends OpportunityCommon {
  kind: 'NEXT_STEP'
  rationale: ShareRationale
  /** The latest lower week's share, the baseline's, in percent, and how many points below. */
  latestShare: number
  baselineShare: number
  pointsBelow: number
  /** Chronic patients seen in the last 90 days with no next step, at the latest lower week's end. */
  withoutNextStep: number
  notes: Array<{ code: 'LAB_ORDERS_NOT_RECORDED' }>
}

export type OpportunitySummary = WaitingTimeSummary | NextStepSummary

export interface WeekFigures {
  week: string
  started: number
  measured: number
  medianMinutes: number | null
}

export interface JudgedWeek extends WeekFigures {
  medianMinutes: number
  judgement: 'WORSE' | 'NORMAL'
}

export interface BreakdownGroup {
  group: string
  baseline: { measured: number; medianMinutes: number }
  compared: { measured: number; medianMinutes: number }
}

export interface Breakdown {
  axis: BreakdownAxis
  groups: BreakdownGroup[]
  hidden: number
}

export type Fact =
  | { claimClass: 'DERIVED_FACT'; code: 'APPOINTMENTS'; usualPerWeek: number; worsePerWeek: number; change: MetricChange }
  | { claimClass: 'DERIVED_FACT'; code: 'CONCENTRATION'; concentration: Concentration }

/** One week's end for the next-step rule: chronic patients seen in 90 days, and of them those with a next step and with a follow-up booked. */
export interface ShareWeek {
  week: string
  recent: number
  nextStep: number
  followUpBooked: number
}

export interface JudgedShareWeek extends ShareWeek {
  /** In percent, to a tenth. */
  share: number
  judgement: 'LOWER' | 'NORMAL'
}

export type ShareFact =
  | { claimClass: 'DERIVED_FACT'; code: 'WITHOUT_NEXT_STEP'; usual: number; latest: number }
  | { claimClass: 'DERIVED_FACT'; code: 'FOLLOW_UP_BOOKED'; usualShare: number | null; latestShare: number | null }

/** The next-step rule's terms. */
export interface NextStepRuleTerms {
  signalRuleId: string
  signalRuleVersion: number
  prioritizationRuleVersion: string
  baselineWeeks: number
  minPatientsPerWeek: number
  lowerByPoints: number
  normalWeeksToResolve: number
}

/** The terms of the rule that finds opportunities: the page states them from here. */
export interface RuleTerms {
  signalRuleId: string
  signalRuleVersion: number
  prioritizationRuleVersion: string
  baselineWeeks: number
  minMeasuredPerWeek: number
  worseByPercent: number
  worseByMinutes: number
  normalWeeksToResolve: number
}

interface DetailCommon {
  claimClass: 'DERIVED_FACT'
  signalRuleId: string
  signalRuleVersion: number
  checkedAt: string
  /** Fewer measured cases than this, and a group is hidden (S0 §17.1). */
  minimumGroupSize: number
  /** The last week that has ended, by its Monday: what "last week" means in a sentence. */
  lastCompleteWeek: string
  /** Connect more data (P3 §3.4): what would say more about the change, each with the ways to get it (DECISIONS.md #45). */
  connectMoreData: InstrumentationGap[]
}

export interface WaitingTimeDetail extends WaitingTimeSummary, DetailCommon {
  baseline: { weeks: WeekFigures[]; medianMinutes: number; measured: number }
  weeks: JudgedWeek[]
  breakdowns: Breakdown[]
  facts: Fact[]
  rule: RuleTerms
}

export interface NextStepDetail extends NextStepSummary, DetailCommon {
  baseline: { weeks: ShareWeek[]; share: number; recent: number }
  weeks: JudgedShareWeek[]
  facts: ShareFact[]
  rule: NextStepRuleTerms
}

export type OpportunityDetail = WaitingTimeDetail | NextStepDetail

export interface OpportunityFeed {
  period: { kind: PeriodKind; first: string; last: string; timezone: string; timezoneKnown: boolean }
  lastCompleteWeek: string
  perspectives: Perspective[]
  rule: RuleTerms
  careRule: NextStepRuleTerms
  items: OpportunitySummary[]
}

export const opportunityKeys = {
  all: (workspaceId: string) => ['workspaces', workspaceId, 'opportunities'] as const,
  feed: (workspaceId: string, filters: OpportunityFilters) =>
    [
      ...opportunityKeys.all(workspaceId),
      'feed',
      filters.period,
      filters.from ?? '',
      filters.to ?? '',
      filters.status,
      filters.domain,
      filters.priority,
    ] as const,
  one: (workspaceId: string, id: string) => [...opportunityKeys.all(workspaceId), 'one', id] as const,
}

const retryUnlessRefused = (failures: number, error: unknown): boolean =>
  !(isApiError(error) && [400, 403, 404].includes(error.statusCode)) && failures < 1

/** The feed (P3 §2), filtered. What the checks found, as they stored it: nothing is computed on reading. */
export const useOpportunities = (
  workspaceId: MaybeRefOrGetter<string>,
  filters: MaybeRefOrGetter<OpportunityFilters>,
  enabled: MaybeRefOrGetter<boolean> = true,
) =>
  useQuery({
    queryKey: computed(() => opportunityKeys.feed(toValue(workspaceId), toValue(filters))),
    queryFn: async () => {
      const { period, from, to, status, domain, priority } = toValue(filters)
      const params = { period, ...(period === 'CUSTOM' ? { from, to } : {}), status, domain, priority }
      return unwrap((await http.get<ApiEnvelope<OpportunityFeed>>(`/workspaces/${toValue(workspaceId)}/opportunities`, { params })).data)
    },
    enabled: computed(() => toValue(enabled)),
    placeholderData: keepPreviousData,
    retry: retryUnlessRefused,
  })

export const useOpportunity = (workspaceId: MaybeRefOrGetter<string>, id: MaybeRefOrGetter<string>) =>
  useQuery({
    queryKey: computed(() => opportunityKeys.one(toValue(workspaceId), toValue(id))),
    queryFn: async () =>
      unwrap(
        (await http.get<ApiEnvelope<OpportunityDetail>>(`/workspaces/${toValue(workspaceId)}/opportunities/${toValue(id)}`)).data,
      ),
    retry: retryUnlessRefused,
  })

/** S0 §8.5: dismissed with a reason; OTHER with a note. */
export const useDismissOpportunity = (workspaceId: MaybeRefOrGetter<string>) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, reason, note }: { id: string; reason: DismissalReason; note?: string }) =>
      unwrap(
        (
          await http.post<ApiEnvelope<OpportunityDetail>>(`/workspaces/${toValue(workspaceId)}/opportunities/${id}/dismiss`, {
            reason,
            ...(note ? { note } : {}),
          })
        ).data,
      ),
    // Refused too: a card someone else just dismissed should stop offering Dismiss.
    onSettled: () => queryClient.invalidateQueries({ queryKey: opportunityKeys.all(toValue(workspaceId)) }),
  })
}
